import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Spinner, Empty } from "../../components/States";
import { fmtRating } from "../../lib/format";

// Catalogue management, built for someone working through a list of edits.
//
// The list stays put and the editor is docked beside it, because an editor that
// expands inline pushes every other row off screen and costs you your place
// after each save. On a narrow screen the same panel becomes a full sheet.

type Row = Awaited<ReturnType<typeof api.adminEntities>>["items"][number];
type Category = { id: string; slug: string; name: string; type: string };

const CHAINS = ["ethereum", "base", "arbitrum", "optimism", "polygon"];

const BLANK_FORM = {
  type: "token" as "token" | "company",
  name: "",
  symbol: "",
  website: "",
  short_desc: "",
  long_desc: "",
  chain: "",
  contract_address: "",
  coingecko_id: "",
  categoryIds: [] as string[],
};
type Form = typeof BLANK_FORM;

const formFor = (row: Row): Form => ({
  type: row.type as "token" | "company",
  name: row.name,
  symbol: row.symbol ?? "",
  website: row.website ?? "",
  short_desc: row.shortDesc ?? "",
  long_desc: row.longDesc ?? "",
  chain: row.chain ?? "",
  contract_address: row.contractAddress ?? "",
  coingecko_id: row.coingeckoId ?? "",
  categoryIds: row.categoryIds,
});

export function Listings() {
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState<Row[] | null>(null);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState<Category[]>([]);

  const [selected, setSelected] = useState<Row | null>(null);
  const [mode, setMode] = useState<"closed" | "edit" | "create">("closed");
  const [form, setForm] = useState<Form>({ ...BLANK_FORM });

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (type) params.set("type", type);
    if (status) params.set("status", status);
    const page = await api.adminEntities(params.toString());
    setRows(page.items);
    setTotal(page.total);
    return page.items;
  }, [q, type, status]);

  useEffect(() => {
    const t = setTimeout(() => void load().catch(() => setRows([])), 200);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api
      .categories()
      .then((c) => setCategories((c as unknown as { items: Category[] }).items ?? []))
      .catch(() => setCategories([]));
    searchRef.current?.focus();
  }, []);

  // Esc closes the panel; "/" jumps to search from anywhere on the page.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const close = () => {
    setMode("closed");
    setSelected(null);
    setError(null);
  };

  /* The panel is sticky under the site header. Until the page is scrolled to
     it, its footer — where Save lives — can sit below the fold. Bring the
     working area to the top on open so every action is reachable. */
  const revealPanel = () => {
    rootRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  };

  const openEdit = (row: Row) => {
    setSelected(row);
    setForm(formFor(row));
    setMode("edit");
    setError(null);
    revealPanel();
  };

  const openCreate = () => {
    setSelected(null);
    setForm({ ...BLANK_FORM });
    setMode("create");
    setError(null);
    revealPanel();
  };

  const run = async (fn: () => Promise<unknown>, message: string, thenClose = false) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setFlash(message);
      const items = await load();
      if (thenClose) close();
      else if (selected) setSelected(items.find((i) => i.id === selected.id) ?? null);
    } catch (err) {
      const e = err as { body?: { error?: { message?: string } } };
      setError(e.body?.error?.message ?? "That did not work. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    if (mode === "create") {
      return run(
        () =>
          api.adminCreateEntity({
            type: form.type,
            name: form.name,
            symbol: form.symbol || null,
            website: form.website,
            shortDesc: form.short_desc,
            chain: form.type === "token" && form.contract_address ? form.chain || "ethereum" : null,
            contractAddress: form.type === "token" ? form.contract_address || null : null,
            categoryIds: form.categoryIds,
          }),
        `Created ${form.name}.`,
        true,
      );
    }
    if (!selected) return;
    return run(
      () =>
        api.adminUpdateEntity(selected.id, {
          name: form.name,
          symbol: form.symbol || null,
          website: form.website || null,
          short_desc: form.short_desc || null,
          long_desc: form.long_desc || null,
          ...(selected.type === "token"
            ? {
                chain: form.chain || null,
                contract_address: form.contract_address || null,
                coingecko_id: form.coingecko_id || null,
              }
            : {}),
          categoryIds: form.categoryIds,
        }),
      `Saved ${form.name}.`,
    );
  };

  const open = mode !== "closed";

  return (
    <div ref={rootRef} className={`admin-listings ${open ? "with-panel" : ""}`}>
      <div className="admin-list-col">
        <div className="admin-toolbar">
          <input
            ref={searchRef}
            className="grow"
            placeholder="Search by name, slug or symbol   ( / )"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <select value={type} onChange={(e) => setType(e.target.value)} aria-label="Filter by type">
            <option value="">All types</option>
            <option value="token">Tokens</option>
            <option value="company">Companies</option>
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
            <option value="">Any status</option>
            <option value="published">Published</option>
            <option value="pending">Awaiting review</option>
            <option value="unlisted">Unlisted</option>
            <option value="rejected">Rejected</option>
          </select>
          <button className="btn btn-primary" onClick={openCreate}>New listing</button>
        </div>

        {flash && !error && <p className="notice notice-ok">{flash}</p>}
        {error && !open && <p className="notice notice-error">{error}</p>}

        {rows === null ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <Empty title="Nothing matches" hint="Try a different search or filter." />
        ) : (
          <>
            <p className="muted admin-count">{total} listing{total === 1 ? "" : "s"}</p>
            <table className="admin-table">
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className={selected?.id === row.id ? "is-selected" : undefined}
                    onClick={() => openEdit(row)}
                  >
                    <td>
                      <button className="link-button text-left" onClick={(e) => { e.stopPropagation(); openEdit(row); }}>
                        <strong>{row.name}</strong>
                        {row.symbol && <span className="muted mono sym">{row.symbol}</span>}
                      </button>
                      <div className="muted slug">/{row.type}/{row.slug}</div>
                    </td>
                    <td className="num nowrap">
                      {row.reviewCount > 0 ? (
                        <>
                          {row.ratingWeighted != null && <strong>{fmtRating(row.ratingWeighted)}★ </strong>}
                          <span className="muted">{row.reviewCount}</span>
                        </>
                      ) : (
                        <span className="muted">-</span>
                      )}
                    </td>
                    <td className="nowrap"><span className={`chip chip-${row.status}`}>{row.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {open && (
        <>
          <div className="panel-scrim" onClick={close} aria-hidden />
          <aside className="admin-panel" aria-label={mode === "create" ? "New listing" : "Edit listing"}>
            <header className="panel-head">
              <div>
                <h2>{mode === "create" ? "New listing" : selected?.name}</h2>
                {mode === "edit" && selected && (
                  <p className="muted panel-sub">
                    /{selected.type}/{selected.slug} · <span className={`chip chip-${selected.status}`}>{selected.status}</span>
                  </p>
                )}
                {mode === "create" && <p className="muted panel-sub">Created by staff, so it publishes immediately.</p>}
              </div>
              <button className="icon-btn" onClick={close} aria-label="Close">✕</button>
            </header>

            <div className="panel-body">
              {error && <p className="notice notice-error">{error}</p>}

              <div className="field-grid">
                {mode === "create" && (
                  <label className="span-2">
                    <span>Type</span>
                    <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as "token" | "company" })}>
                      <option value="token">Token</option>
                      <option value="company">Company</option>
                    </select>
                  </label>
                )}

                <label className={form.type === "token" ? "" : "span-2"}>
                  <span>Name</span>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </label>

                {form.type === "token" && (
                  <label>
                    <span>Symbol</span>
                    <input value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })} />
                  </label>
                )}

                <label className="span-2">
                  <span>Website</span>
                  <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" />
                </label>

                <label className="span-2">
                  <span>Short description <span className="muted">{form.short_desc.length}/200</span></span>
                  <textarea rows={2} maxLength={200} value={form.short_desc}
                    onChange={(e) => setForm({ ...form, short_desc: e.target.value })} />
                </label>

                {mode === "edit" && (
                  <label className="span-2">
                    <span>Long description</span>
                    <textarea rows={4} value={form.long_desc}
                      onChange={(e) => setForm({ ...form, long_desc: e.target.value })} />
                  </label>
                )}

                {form.type === "token" && (
                  <>
                    <label>
                      <span>Chain</span>
                      <select value={form.chain} onChange={(e) => setForm({ ...form, chain: e.target.value })}>
                        <option value="">-</option>
                        {CHAINS.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </label>
                    <label>
                      <span>CoinGecko id</span>
                      <input value={form.coingecko_id} placeholder="chainlink"
                        onChange={(e) => setForm({ ...form, coingecko_id: e.target.value })} />
                    </label>
                    <label className="span-2">
                      <span>
                        Contract address
                        {form.contract_address && (
                          <a className="field-link" href={`https://etherscan.io/token/${form.contract_address}`}
                             target="_blank" rel="noopener noreferrer nofollow">check on explorer ↗</a>
                        )}
                      </span>
                      <input className="mono" spellCheck={false} placeholder="0x…" value={form.contract_address}
                        onChange={(e) => setForm({ ...form, contract_address: e.target.value })} />
                    </label>
                  </>
                )}

                <CategoryPicker
                  categories={categories.filter((c) => c.type === form.type)}
                  selected={form.categoryIds}
                  onChange={(ids) => setForm({ ...form, categoryIds: ids })}
                />
              </div>
            </div>

            <footer className="panel-foot">
              <button className="btn btn-primary" onClick={save} disabled={busy || !form.name}>
                {busy ? "Saving…" : mode === "create" ? "Create & publish" : "Save"}
              </button>

              {mode === "edit" && selected && (
                <>
                  {selected.status === "pending" ? (
                    <>
                      <button className="btn btn-ghost" disabled={busy}
                        onClick={() => run(() => api.adminApproveEntity(selected.id), `Published ${selected.name}.`)}>
                        Approve
                      </button>
                      <button className="btn btn-ghost" disabled={busy}
                        onClick={() => run(() => api.adminRejectEntity(selected.id, "not accepted"), `Rejected ${selected.name}.`)}>
                        Reject
                      </button>
                    </>
                  ) : selected.status === "published" ? (
                    <button className="btn btn-ghost" disabled={busy}
                      onClick={() => run(() => api.adminSetEntityStatus(selected.id, "unlisted"), `Unlisted ${selected.name}.`)}>
                      Unlist
                    </button>
                  ) : (
                    <button className="btn btn-ghost" disabled={busy}
                      onClick={() => run(() => api.adminSetEntityStatus(selected.id, "published"), `Published ${selected.name}.`)}>
                      Publish
                    </button>
                  )}

                  <Link className="btn btn-ghost" to={`/${selected.type}/${selected.slug}`}>View</Link>

                  <button className="btn btn-ghost danger push-right" disabled={busy}
                    onClick={() => {
                      if (confirm(`Delete ${selected.name}? Its reviews stay in the database but the page goes away.`)) {
                        void run(() => api.adminDeleteEntity(selected.id), `Deleted ${selected.name}.`, true);
                      }
                    }}>
                    Delete
                  </button>
                </>
              )}
            </footer>
          </aside>
        </>
      )}
    </div>
  );
}

function CategoryPicker({
  categories,
  selected,
  onChange,
}: {
  categories: Category[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  if (categories.length === 0) return null;
  return (
    <div className="span-2">
      <span className="label-text">Categories</span>
      <div className="chip-picker">
        {categories.map((c) => {
          const on = selected.includes(c.id);
          return (
            <button
              key={c.id}
              type="button"
              className={`toggle-chip ${on ? "is-on" : ""}`}
              aria-pressed={on}
              onClick={() => onChange(on ? selected.filter((x) => x !== c.id) : [...selected, c.id])}
            >
              {on && <span aria-hidden>✓ </span>}
              {c.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
